/*
 * IV Fall Flash - spec .planning/imperil-fall-flash/SPEC.md.
 * 1. Deadline: every [data-iv-flash] node disappears at the earliest data-iv-end, without a
 *    reload (cached HTML, long-open tabs). 2. Clock in the sitewide bar for the final 72 h.
 * 3. PDP "add both": one /cart/add.js call, drawer repaint, honest partial-failure message.
 * Delegated from document (the drawer re-renders wholesale). Deliberately does NOT
 * publish(cartUpdate): Dawn's CartItems subscriber would refetch cart sections over the drawer
 * (midknight 2026-08-04). It only subscribes, read-only, to keep the PDP mode fresh - via the
 * top-level `subscribe` / `PUB_SUB_EVENTS` bindings, which are NOT on `window` (Codex R2 #2).
 */
(function () {
  'use strict';
  if (window.__ivFlashInit) return;
  window.__ivFlashInit = true;

  function parseTime(iso) { var t = Date.parse(iso || ''); return isNaN(t) ? null : t; }
  function cartUrl() { return (window.routes && window.routes.cart_url) || '/cart'; }
  function addUrl() { return ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js'; }

  // ------------------------------------------------------------------ deadline
  function earliestEnd() {
    var min = null;
    document.querySelectorAll('[data-iv-end]').forEach(function (n) {
      var t = parseTime(n.getAttribute('data-iv-end'));
      if (t !== null && (min === null || t < min)) min = t;
    });
    return min;
  }
  function hideAll() {
    document.querySelectorAll('[data-iv-flash]').forEach(function (n) { n.remove(); });
    document.documentElement.classList.add('iv-flash-ended');
  }
  var endTimer = null;
  function armDeadline() {
    var end = earliestEnd();
    if (end === null) return;
    var ms = end - Date.now();
    if (ms <= 0) { hideAll(); return; }
    clearTimeout(endTimer);
    endTimer = setTimeout(hideAll, Math.min(ms, 2147483000));
  }

  // --------------------------------------------------------------------- clock
  function pad(n) { return n < 10 ? '0' + n : String(n); }
  function renderClocks() {
    document.querySelectorAll('[data-iv-flash-bar]').forEach(function (bar) {
      var endEl = bar.querySelector('[data-iv-flash-end]');
      var end = parseTime(bar.getAttribute('data-iv-end'));
      var from = parseTime(bar.getAttribute('data-clock-from'));
      if (!endEl || end === null || from === null) return;
      var now = Date.now();
      if (now < from || now >= end) return;
      var left = end - now;
      var d = Math.floor(left / 86400000);
      var h = Math.floor((left % 86400000) / 3600000);
      var m = Math.floor((left % 3600000) / 60000);
      var fmt = d > 0 ? bar.getAttribute('data-clock-format') : bar.getAttribute('data-clock-format-lt-day');
      if (!fmt) return;
      endEl.textContent = fmt.replace('{d}', String(d)).replace('{h}', String(h)).replace('{m}', pad(m));
      bar.classList.add('iv-flash-bar--clock');
    });
  }

  // --------------------------------------------------------------- PDP: modes
  function poolOf(root) { return (root.getAttribute('data-pool') || '').split(',').filter(Boolean); }
  function fetchCart() {
    // `iv_flash=1` changes nothing server-side; it marks OUR reads in network logs and lets the
    // race test delay exactly our boot read, not some app's /cart.js call.
    return fetch(cartUrl() + '.js?iv_flash=1', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }
  // PURE: this block's state for THIS cart - the same rules as iv-flash-state.
  function evaluate(root, cart) {
    var pool = poolOf(root), handle = root.getAttribute('data-product-handle');
    var prefix = root.getAttribute('data-title-prefix') || '';
    var poolUnits = 0, thisUnits = 0, pairs = 0, flashCents = 0;
    (cart.items || []).forEach(function (it) {
      if (pool.indexOf(it.handle) === -1) return;
      poolUnits += it.quantity;
      if (it.handle === handle) thisUnits += it.quantity;
      var flashPaid = false;
      (it.line_level_discount_allocations || []).forEach(function (a) {
        if (prefix && a.discount_application && (a.discount_application.title || '').indexOf(prefix) === 0) {
          flashCents += a.amount || 0;
          // Controller fix 2026-09-25 (verified live): BXGY allocates to BOTH the buy line
          // (amount 0) and the get line (amount > 0) under the same title. Counting either
          // match double-counts pairs (2 lines, 1 real pair) - only the positive-amount side
          // is the "1 unit = 1 pair" side. Same rule as snippets/iv-flash-state.liquid.
          if ((a.amount || 0) > 0) flashPaid = true;
        }
      });
      if (flashPaid) pairs += it.quantity; // BXGY discounts one unit per pair
    });
    return {
      // Codex R1 #2: with a pair already in the cart, "both for $99" holds only if every_pair is proven
      hidden: pairs >= 1 && root.getAttribute('data-every-pair') !== 'true',
      partnerOnly: poolUnits === 1 && thisUnits === 1,
      // Codex R2 #1: another discount in the cart can be displaced, so no "+$X" wording
      deltaOk: ((cart.total_discount || 0) - flashCents) <= 0,
    };
  }
  function paintMode(root, st) {
    root.hidden = st.hidden;
    var claim = root.querySelector('[data-iv-flash-claim]');
    if (claim) claim.textContent = claim.getAttribute(st.deltaOk ? 'data-claim' : 'data-claim-nodelta');
    var form = root.querySelector('[data-iv-flash-both]');
    if (!form) return;
    form.setAttribute('data-mode', st.partnerOnly ? 'partner' : 'both');
    var btn = form.querySelector('[data-iv-flash-add]');
    var txt = btn && btn.querySelector('[data-iv-flash-add-text]');
    var label = !st.partnerOnly ? 'data-label-both' : (st.deltaOk ? 'data-label-partner' : 'data-label-partner-nodelta');
    if (txt) txt.textContent = btn.getAttribute(label);
  }
  // Codex R3 #1: refreshes can overlap (boot + cartUpdate + iv events) and answer out of order.
  // Only the NEWEST request may paint; a superseded response is dropped.
  var refreshSeq = 0;
  // B-052 (Derrick 2026-09-25 "everything takes forever to add"): the newest painted cart is kept so
  // "Add both" can post in ONE round trip. It is only trusted while no newer refresh is in flight.
  var lastCart = null, lastCartSeq = -1;
  function refreshPdpModes() {
    var roots = document.querySelectorAll('[data-iv-flash-pdp]');
    if (!roots.length) return Promise.resolve();
    var mine = ++refreshSeq;
    return fetchCart()
      .then(function (cart) {
        if (mine !== refreshSeq) return;
        lastCart = cart; lastCartSeq = mine;
        roots.forEach(function (root) { paintMode(root, evaluate(root, cart)); });
      })
      .catch(function () { /* keep the server-rendered state; the add path re-validates anyway */ });
  }

  // ------------------------------------------------- PDP: partner size follows main
  function syncPartnerSize(root) {
    var mainForm = document.getElementById(root.getAttribute('data-main-form'));
    var input = mainForm && mainForm.querySelector('[name="id"]');
    var sel = root.querySelector('[data-iv-flash-partner]');
    if (!input || !sel || sel.dataset.touched === '1') return;
    var titles = {};
    try { titles = JSON.parse(root.getAttribute('data-main-variants') || '{}'); } catch (e) { return; }
    var want = titles[input.value];
    if (!want) return;
    for (var i = 0; i < sel.options.length; i++) {
      var o = sel.options[i];
      if (o.getAttribute('data-title') === want && !o.disabled) { sel.value = o.value; return; }
    }
  }

  // --------------------------------------------------------- PDP: add both
  function setBusy(btn, busy) {
    if (!btn) return;
    btn.disabled = busy;
    btn.classList.toggle('is-loading', busy);
  }
  function showError(el, msg) { if (!el) return; el.textContent = msg || ''; el.hidden = !msg; }
  function paintDrawer(json) {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && json && json.sections && typeof drawer.renderContents === 'function') {
      drawer.classList.remove('is-empty'); // stock Dawn bug, see iv-cart-upsell.js
      drawer.renderContents(json);
      return true;
    }
    return false;
  }
  function refreshDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.getSectionsToRender !== 'function') { window.location.href = cartUrl(); return Promise.resolve(); }
    var ids = drawer.getSectionsToRender().map(function (s) { return s.id; }).join(',');
    return fetch(window.location.pathname + '?sections=' + ids)
      .then(function (r) { return r.json(); })
      .then(function (sections) { paintDrawer({ sections: sections }); });
  }
  function qtyOf(cart, id) {
    var n = 0;
    ((cart && cart.items) || []).forEach(function (it) { if (it.variant_id === id) n += it.quantity; });
    return n;
  }
  function submitBoth(form) {
    var root = form.closest('[data-iv-flash-pdp]');
    var btn = form.querySelector('[data-iv-flash-add]');
    var errEl = root.querySelector('[data-iv-flash-error]');
    if (btn && btn.disabled) return;           // one add at a time
    setBusy(btn, true);
    showError(errEl, '');
    // Codex R3 #1: never trust the painted MODE - decide from a cart snapshot. B-052: that snapshot is
    // the newest painted cart when no refresh is in flight (1 round trip); otherwise re-read the cart.
    var before = null;
    var items = null;
    (lastCart && lastCartSeq === refreshSeq ? Promise.resolve(lastCart) : fetchCart())
      .then(function (cart) {
        before = cart;
        var st = evaluate(root, cart);
        refreshSeq++;                            // anything still in flight is now superseded
        paintMode(root, st);
        if (st.hidden) { setBusy(btn, false); return null; }
        var mainForm = document.getElementById(root.getAttribute('data-main-form'));
        var mainInput = mainForm && mainForm.querySelector('[name="id"]');
        var mainId = Number((mainInput && mainInput.value) || form.querySelector('[data-iv-flash-main-id]').value);
        var partnerId = Number(form.querySelector('[data-iv-flash-partner]').value);
        items = st.partnerOnly ? [{ id: partnerId, quantity: 1 }]
                               : [{ id: mainId, quantity: 1 }, { id: partnerId, quantity: 1 }];
        var drawer = document.querySelector('cart-drawer');
        var body = { items: items };
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          body.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
          body.sections_url = window.location.pathname;
        }
        return fetch(addUrl(), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
          .then(function (res) { return res.json().then(function (json) { return { ok: res.ok, json: json }; }); });
      })
      .then(function (r) {
        if (!r) return null;                     // promise no longer held; nothing was added
        setBusy(btn, false); // clear BEFORE the paint that consumes it (skill lesson #16)
        if (r.ok && paintDrawer(r.json)) {
          // ONE background true-up: the iv:flash:added listener runs refreshPdpModes (it used to run twice)
          lastCart = null;
          document.dispatchEvent(new CustomEvent('iv:flash:added', { detail: { items: items } }));
          return null;
        }
        return reconcile(items, before, errEl, r.json);
      })
      .catch(function () { setBusy(btn, false); return items ? reconcile(items, before, errEl, null) : refreshPdpModes(); });
  }
  function flashForm(errEl) {
    var root = errEl && errEl.closest('[data-iv-flash-pdp]');
    return root ? root.querySelector('form[data-iv-flash-both]') : null;
  }
  function reconcile(items, before, errEl, json) {
    // /cart/add.js with several items is not atomic: say what actually landed, never "both".
    return fetch(cartUrl() + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (after) {
        var landed = items.filter(function (it) { return qtyOf(after, it.id) > qtyOf(before, it.id); }).length;
        var form = flashForm(errEl);
        var base = (json && (json.description || json.message)) || '';
        var partial = form ? form.getAttribute('data-error-partial') : null;
        // Nothing landed: the copy batch's error_add (final review 2026-09-25), Shopify's text only as a fallback.
        var none = form ? form.getAttribute('data-error-add') : null;
        showError(errEl, landed > 0 && partial ? partial.replace('{n}', String(landed)) : (none || base || 'Could not add. Try again.'));
        return landed > 0 ? refreshDrawer().then(refreshPdpModes) : refreshPdpModes();
      })
      .catch(function () { var f = flashForm(errEl); showError(errEl, (f && f.getAttribute('data-error-add')) || 'Could not add. Try again.'); });
  }

  // --------------------------------------------------------------- wiring
  document.addEventListener('submit', function (evt) {
    var form = evt.target.closest && evt.target.closest('[data-iv-flash-both]');
    if (!form) return;
    evt.preventDefault();
    submitBoth(form);
  });
  document.addEventListener('change', function (evt) {
    var t = evt.target;
    if (t && t.matches && t.matches('[data-iv-flash-partner]')) { t.dataset.touched = '1'; return; }
    if (t && t.name === 'id') document.querySelectorAll('[data-iv-flash-pdp]').forEach(syncPartnerSize);
  });
  ['iv:flash:added', 'iv:upsell:added'].forEach(function (e) { document.addEventListener(e, refreshPdpModes); });
  function boot() {
    armDeadline();
    renderClocks();
    setInterval(renderClocks, 15000);
    document.querySelectorAll('[data-iv-flash-pdp]').forEach(syncPartnerSize);
    refreshPdpModes();
    // Codex R2 #2: Dawn's constants.js declares `const PUB_SUB_EVENTS` at top level - a global
    // LEXICAL binding, NOT a window property - so `window.PUB_SUB_EVENTS` is undefined and a
    // window-guarded subscribe silently never fires. Reference the bindings directly; both
    // scripts are deferred earlier in <head>, so they exist by the time this deferred file runs.
    /* global subscribe, PUB_SUB_EVENTS */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { refreshPdpModes(); });
    }
  }
  document.addEventListener('visibilitychange', function () { if (!document.hidden) { armDeadline(); renderClocks(); } });
  window.addEventListener('pageshow', function (e) { if (e.persisted) { armDeadline(); renderClocks(); refreshPdpModes(); } });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
